import { ConsentConfig, CountryConsent } from 'src/app/core/models';
import { IConsent } from 'src/app/core/models/consent';

/**
 * Formats the consents based on the provided form values and country consents.
 *
 * Iterates through the given `consents` array, filters out consents whose purposes are not selected
 * in the `formValue`, and constructs an array of formatted consent objects containing the collection point
 * information and the selected purposes.
 *
 * @param formValue - An object mapping purpose IDs to boolean values indicating whether each purpose is selected.
 * @param consents - An array of `CountryConsent` objects representing available consents and their purposes.
 * @returns An array of `IConsent` objects, each containing the collection point GUID, version, and the array of selected purpose IDs.
 */
export function getFormattedConsents(
  formValue: { [purposeId: string]: boolean },
  consents: CountryConsent[],
): IConsent[] {
  if (
    !formValue ||
    !consents ||
    Object.keys(formValue).length === 0 ||
    consents.length === 0
  ) {
    return [];
  }

  const result: IConsent[] = [];

  consents
    .filter(
      (consent) =>
        consent.consentPurposes?.length > 0 &&
        consent.consentPurposes.some(
          (purpose) => formValue[purpose.purposeId] === true,
        ),
    )
    .forEach((consent) => {
      const purposesArray = consent.consentPurposes
        .filter((purpose) => formValue[purpose.purposeId])
        .map((purpose) => purpose.purposeId);

      if (purposesArray.length === 0) {
        return;
      }

      result.push({
        collection_point_guid: consent.consentCollectionPointId,
        collection_point_version: consent.consentCollectionPointVersion,
        purposesArray,
      });
    });

  return result;
}

/**
 * Type guard to determine if a given consent configuration is a `CountryConsent`.
 *
 * Checks if the provided `consent` object contains the `consentPurposes` property,
 * which is indicative of the `CountryConsent` type.
 *
 * @param consent - The consent configuration object to check.
 * @returns `true` if the object is a `CountryConsent`, otherwise `false`.
 */
export function isCountryConsent(
  consent: ConsentConfig,
): consent is CountryConsent {
  return consent && Object.hasOwn(consent, 'consentPurposes');
}
